import React, { useState, useEffect, useCallback } from 'react';
import { Shield, CheckCircle, Upload, Image as ImageIcon, ArrowLeft, QrCode } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRScanner from '../components/QRScanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function extractWarrantyNumber(text) {
  if (!text) return '';
  const trimmed = String(text).trim();
  try {
    const u = new URL(trimmed);
    const nr = u.searchParams.get('nr');
    if (nr) return nr.trim().toUpperCase();
  } catch { /* not a URL */ }
  const m = trimmed.match(/GDC-[A-Z0-9]+-\d+/i);
  if (m) return m[0].toUpperCase();
  return trimmed.toUpperCase();
}

function WarrantyRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    warranty_number: '',
    customer_name: '',
    phone: '',
    address: '',
    email: '',
    gravestone_image: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanFlash, setScanFlash] = useState('');

  // Pre-fill warranty number from ?nr= query parameter (e.g. via QR scan)
  useEffect(() => {
    const nr = searchParams.get('nr');
    if (nr) {
      setFormData(prev => ({ ...prev, warranty_number: nr.trim().toUpperCase() }));
    }
  }, [searchParams]);

  const handleScanned = useCallback((text) => {
    const wn = extractWarrantyNumber(text);
    setFormData(prev => ({ ...prev, warranty_number: wn }));
    setShowScanner(false);
    setScanFlash(`Garantinummer fylt inn: ${wn}`);
    setTimeout(() => setScanFlash(''), 2500);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const TARGET_BYTES = 800_000;
      const MAX_ITER = 6;
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        let maxWidth = 1600;
        let quality = 0.75;
        let attempt = 0;
        const tryCompress = () => {
          try {
            const canvas = document.createElement('canvas');
            let { width, height } = img;
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            if (dataUrl.length <= TARGET_BYTES || attempt >= MAX_ITER) {
              URL.revokeObjectURL(objectUrl);
              resolve(dataUrl);
              return;
            }
            attempt += 1;
            if (quality > 0.4) quality -= 0.1;
            else maxWidth = Math.max(800, Math.floor(maxWidth * 0.85));
            setTimeout(tryCompress, 0);
          } catch (e) { URL.revokeObjectURL(objectUrl); reject(e); }
        };
        tryCompress();
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('img load failed')); };
      img.src = objectUrl;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    try {
      const compressed = await compressImage(file);
      setFormData(prev => ({ ...prev, gravestone_image: compressed }));
    } catch {
      // Fallback to raw base64 if compression fails for some reason.
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setFormData(prev => ({ ...prev, gravestone_image: reader.result }));
      };
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setFormData({
      ...formData,
      gravestone_image: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${API}/warranty/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      // Always try to parse the body — many platforms return JSON details on errors too.
      let payload = null;
      try { payload = await response.json(); } catch { /* non-JSON */ }

      if (response.ok) {
        setIsSuccess(true);
        setSubmitMessage('Gratulerer! Din garanti er nå registrert. Du vil motta garantibeviset på e-post om noen minutter.');
        setFormData({
          warranty_number: '',
          customer_name: '',
          phone: '',
          address: '',
          email: '',
          gravestone_image: ''
        });
        setSelectedFile(null);
      } else if (payload && payload.detail) {
        setSubmitMessage(payload.detail);
      } else {
        setSubmitMessage(`Serveren svarte med feil ${response.status}. Sjekk nummeret og prøv igjen, eller ta kontakt om problemet vedvarer.`);
      }
    } catch (error) {
      console.error('Warranty register fetch error:', error);
      const detail = error && error.message ? error.message : String(error);
      setSubmitMessage(`Kunne ikke nå serveren (${detail}). Sjekk nettforbindelsen og prøv igjen.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {showScanner && (
        <QRScanner onScan={handleScanned} onClose={() => setShowScanner(false)} />
      )}
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-emerald-100 hover:text-white mb-6 transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={20} />
            Tilbake til forsiden
          </button>
          
          <div className="flex items-center gap-4 mb-6">
            <Shield size={48} className="text-amber-300" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Registrer din garanti
              </h1>
              <p className="text-emerald-100 text-lg mt-2">
                Få 10 års garanti på din DC-Protect beskyttelse
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!isSuccess ? (
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Fyll ut registreringsskjema
              </h2>
              <p className="text-stone-600">
                Etter registrering vil du motta et garantibevis på e-post med alle detaljer om din 10 års garanti.
              </p>
            </div>

            <form onSubmit={handleSubmit} data-testid="warranty-registration-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="warranty_number" className="block text-sm font-medium text-stone-700 mb-2">
                    Garantinummer *
                  </label>
                  <p className="text-xs text-stone-500 mb-2">
                    Du finner garantinummeret og en QR-kode i ditt gjør-det-selv kit. Du kan skrive bare sifrene (f.eks. <strong>0145</strong>) eller hele nummeret (<strong>GDC-2025-0145</strong>).
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="warranty_number"
                      name="warranty_number"
                      required
                      value={formData.warranty_number}
                      onChange={handleInputChange}
                      placeholder="Eks: GDC-2026-0001"
                      className="flex-1 px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent uppercase"
                      data-testid="warranty-number-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="flex items-center gap-2 px-4 py-3 bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 font-medium whitespace-nowrap"
                      data-testid="scan-qr-btn"
                    >
                      <QrCode size={20} /> Skann QR
                    </button>
                  </div>
                  {scanFlash && (
                    <p className="text-xs text-emerald-700 mt-2 font-medium" data-testid="scan-flash">{scanFlash}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="customer_name" className="block text-sm font-medium text-stone-700 mb-2">
                    Ditt fulle navn *
                  </label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    required
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                    Mobilnummer *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Eks: 12345678"
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-stone-700 mb-2">
                    Adresse (valgfritt)
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Gate, postnummer, sted"
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                    data-testid="address-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                    E-postadresse *
                  </label>
                  <p className="text-xs text-stone-500 mb-2">
                    Garantibeviset sendes til denne e-postadressen
                  </p>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Bilde av gravsteinen *
                  </label>
                  <p className="text-xs text-stone-500 mb-2">
                    Last opp et tydelig bilde av gravsteinen etter at du har påført DC-Protect beskyttelsen
                  </p>
                  <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-emerald-800 transition-colors">
                    <input
                      type="file"
                      id="gravestone_image"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      required={!selectedFile}
                      data-testid="gravestone-image-input"
                    />
                    <label
                      htmlFor="gravestone_image"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="text-stone-400" size={32} />
                      <p className="text-sm text-stone-600">
                        Klikk for å laste opp bilde
                      </p>
                      <p className="text-xs text-stone-500">
                        PNG, JPG eller JPEG
                      </p>
                    </label>
                  </div>
                  {selectedFile && (
                    <div className="mt-4 bg-stone-50 p-4 rounded-lg" data-testid="uploaded-image-preview">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <ImageIcon className="text-emerald-800" size={24} />
                          <div>
                            <p className="text-sm font-medium text-stone-900">{selectedFile.name}</p>
                            <p className="text-xs text-stone-500">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          data-testid="remove-image-btn"
                        >
                          Fjern
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold text-stone-900 mb-2 flex items-center gap-2">
                    <Shield className="text-amber-700" size={20} />
                    Viktig informasjon
                  </h3>
                  <ul className="text-sm text-stone-700 space-y-1 ml-6 list-disc">
                    <li>Garantien starter fra registreringsdato</li>
                    <li>Du mottar garantibeviset på e-post innen få minutter</li>
                    <li>Garantien gjelder i 10 år fra registreringsdato</li>
                    <li>Les garantibetingelsene som følger med garantibeviset</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-warranty-btn"
                >
                  {isSubmitting ? 'Registrerer...' : 'Registrer garanti'}
                </button>

                {submitMessage && !isSuccess && (
                  <p className="text-center text-red-600" data-testid="error-message">
                    {submitMessage}
                  </p>
                )}
              </div>
            </form>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 text-center" data-testid="success-message">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="text-emerald-800" size={48} />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Garantien er registrert!
            </h2>
            <p className="text-lg text-stone-600 mb-6">
              {submitMessage}
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-stone-900 mb-3">Hva skjer nå?</h3>
              <ul className="text-sm text-stone-700 space-y-2 text-left">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-emerald-800 flex-shrink-0 mt-0.5" size={16} />
                  <span>Garantibeviset sendes til din e-post</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-emerald-800 flex-shrink-0 mt-0.5" size={16} />
                  <span>Du har 10 års garanti fra i dag</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-emerald-800 flex-shrink-0 mt-0.5" size={16} />
                  <span>Les garantibetingelsene i e-posten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-emerald-800 flex-shrink-0 mt-0.5" size={16} />
                  <span>Ta vare på garantibeviset</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
              data-testid="back-home-success-btn"
            >
              Tilbake til forsiden
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WarrantyRegistration;
